import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Campaign, CampaignWithSessionCount } from 'api/types/campaign';
import { PaginatedResponse } from 'api/types/pagination';
import CampaignsApi from 'api/campaigns';

interface CampaignState {
  campaigns: CampaignWithSessionCount[];
  currentCampaign: CampaignWithSessionCount | null;
  loading: boolean;
  error?: string;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    hasMore: boolean;
  };
  sorting: {
    sortBy: 'name_asc' | 'name_desc' | 'created_date' | 'latest_session';
  };
}

const initialState: CampaignState = {
  campaigns: [],
  currentCampaign: null,
  loading: false,
  error: undefined,
  pagination: {
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalRecords: 0,
    hasMore: false
  },
  sorting: {
    sortBy: 'created_date'
  }
};

export const fetchCampaigns = createAsyncThunk<
  PaginatedResponse<CampaignWithSessionCount>,
  { limit?: number; page?: number; sortBy?: string; active?: boolean },
  { rejectValue: string }
>(
  'campaign/fetchCampaigns',
  async ({ limit = 10, page = 1, sortBy, active }, { rejectWithValue }) => {
    try {
      const response = await CampaignsApi.fetchCampaigns(limit, page, sortBy, active);
      if (!response.data) {
        throw new Error('No data found');
      }
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }

      return response.data;
    } catch (err: unknown) {
      let errorMessage = 'Failed to fetch campaigns';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchActiveCampaigns = createAsyncThunk<
  PaginatedResponse<CampaignWithSessionCount>,
  void,
  { rejectValue: string }
>('campaign/fetchActiveCampaigns', async (_, { rejectWithValue }) => {
  try {
    const response = await CampaignsApi.fetchActiveCampaigns();
    if (!response.data) {
      throw new Error('No data found');
    }
    if (response.status !== 200) {
      throw new Error(`Error: ${response.status}`);
    }

    // Transform to match the expected paginated response format
    return {
      data: response.data as CampaignWithSessionCount[],
      currentPage: 1,
      pageSize: response.data.length,
      totalPages: 1,
      totalRecords: response.data.length,
      hasMore: false
    };
  } catch (err: unknown) {
    let errorMessage = 'Failed to fetch active campaigns';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return rejectWithValue(errorMessage);
  }
});

export const fetchCampaignById = createAsyncThunk<
  CampaignWithSessionCount,
  string,
  { rejectValue: string }
>('campaign/fetchCampaignById', async (campaignId, { rejectWithValue }) => {
  try {
    const response = await CampaignsApi.fetchCampaignById(campaignId);
    if (!response.data) {
      throw new Error('No data found');
    }
    if (response.status !== 200) {
      throw new Error(`Error: ${response.status}`);
    }

    return response.data;
  } catch (err: unknown) {
    let errorMessage = 'Failed to fetch campaign';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return rejectWithValue(errorMessage);
  }
});

export const createCampaign = createAsyncThunk<
  Campaign,
  Partial<Campaign>,
  { rejectValue: string }
>('campaign/createCampaign', async (campaignData, { rejectWithValue }) => {
  try {
    const response = await CampaignsApi.createCampaign(campaignData);
    if (!response.data) {
      throw new Error('No data found');
    }
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Error: ${response.status}`);
    }
    return response.data;
  } catch (err: unknown) {
    let errorMessage = 'Failed to create campaign';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return rejectWithValue(errorMessage);
  }
});

export const updateCampaign = createAsyncThunk<
  Campaign,
  { id: string; data: Partial<Campaign> },
  { rejectValue: string }
>('campaign/updateCampaign', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await CampaignsApi.updateCampaign(id, data);
    if (!response.data) {
      throw new Error('No data found');
    }
    if (response.status !== 200) {
      throw new Error(`Error: ${response.status}`);
    }
    return response.data;
  } catch (err: unknown) {
    let errorMessage = 'Failed to update campaign';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return rejectWithValue(errorMessage);
  }
});

const campaignSlice = createSlice({
  name: 'campaign',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when page size changes
    },
    setSortBy: (
      state,
      action: PayloadAction<'name_asc' | 'name_desc' | 'created_date' | 'latest_session'>
    ) => {
      state.sorting.sortBy = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when sorting changes
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchCampaigns.fulfilled,
        (state, action: PayloadAction<PaginatedResponse<CampaignWithSessionCount>>) => {
          state.loading = false;
          state.campaigns = action.payload.data;
          state.pagination = {
            currentPage: action.payload.currentPage,
            pageSize: action.payload.pageSize,
            totalPages: action.payload.totalPages,
            totalRecords: action.payload.totalRecords,
            hasMore: action.payload.hasMore
          };
        }
      )
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });

    builder
      .addCase(fetchActiveCampaigns.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchActiveCampaigns.fulfilled,
        (state, action: PayloadAction<PaginatedResponse<CampaignWithSessionCount>>) => {
          state.loading = false;
          state.campaigns = action.payload.data;
          state.pagination = {
            currentPage: action.payload.currentPage,
            pageSize: action.payload.pageSize,
            totalPages: action.payload.totalPages,
            totalRecords: action.payload.totalRecords,
            hasMore: action.payload.hasMore
          };
        }
      )
      .addCase(fetchActiveCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });

    builder
      .addCase(createCampaign.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.loading = false;
        state.campaigns.push(action.payload as CampaignWithSessionCount);
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });

    builder
      .addCase(updateCampaign.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(updateCampaign.fulfilled, (state, action: PayloadAction<Campaign>) => {
        state.loading = false;
        const index = state.campaigns.findIndex((campaign) => campaign.id === action.payload.id);
        if (index !== -1) {
          state.campaigns[index] = { ...state.campaigns[index], ...action.payload };
        }
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });

    builder
      .addCase(fetchCampaignById.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchCampaignById.fulfilled,
        (state, action: PayloadAction<CampaignWithSessionCount>) => {
          state.loading = false;
          state.currentCampaign = action.payload;
        }
      )
      .addCase(fetchCampaignById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      });
  }
});

export const { setCurrentPage, setPageSize, setSortBy } = campaignSlice.actions;
export default campaignSlice.reducer;
